import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Download } from 'react-feather';
import cn from 'classnames';
import Clipboard from 'clipboard';
import request from 'utils/request_ajax';
import { routes } from 'constants/config';
import { Modal, ButtonGroup, Button } from 'ui';
import RadioFieldGroup from 'components/shared/RadioFieldGroup/index';
import RadioField from 'components/shared/RadioFieldGroup/RadioField';
import Fade from 'components/shared/animated/Fade';

const ModalContent = ({ lesson, toggleModal, urls, isPublic, setIsPublic }) => {
  const urlTextRef = useRef(null);
  const copyTextButtonRef = useRef(null);
  const [copiedLinkSuccess, setCopiedLinkSuccess] = useState(null);
  const [error, setError] = useState(null);
  const isArchived = lesson.archivedAt !== null;

  useEffect(() => {
    if (urlTextRef.current) {
      new Clipboard(urlTextRef.current).on('success', () => {
        setCopiedLinkSuccess(true);
      });
    }
    if (copyTextButtonRef.current) {
      new Clipboard(copyTextButtonRef.current).on('success', () => {
        setCopiedLinkSuccess(true);
      });
    }
  }, [urlTextRef, copyTextButtonRef]);

  const copyLink = () => {
    urlTextRef.current.select();
  };

  const publicChange = e => {
    const publicitySetting = e.target.value;
    setIsPublic(JSON.parse(publicitySetting));
    setError(false);
    request
      .put(routes.LESSON(lesson.id), {
        lesson: {
          public: publicitySetting,
        },
      })
      .fail(response => setError(response));
  };

  const displayLinkClass = cn('mt-12 mb-16 ai-c', {
    'd-f': isPublic,
    'd-n': !isPublic,
  });

  return (
    <>
      <Modal.Content>
        <div className="mb-24">
          <h1 className="ff-ss lh-48 fz-32 c-brand-night">
            {t('lesson_overview.share_lesson')}
          </h1>
        </div>
        <div className="d-f fd-c p-16">
          {!isArchived && (
            <>
              <h1 className="ff-ss lh-32 fz-18 c-gray-dark fw-b">
                {t('lesson_overview.privacy')}
              </h1>
              <RadioFieldGroup
                className="ff-ss mt-12"
                selectedValue={isPublic}
                name="type"
                onChange={publicChange}
              >
                <RadioField className="c-p" value={false}>
                  <b className="c-brand-night fz-16">
                    {t('lesson_overview.share_lesson_private.title')}
                  </b>
                  <p className="c-brand-gray-darker fz-16 mb-0 ml-24">
                    {t('lesson_overview.share_lesson_private.description')}
                  </p>
                </RadioField>
                <RadioField className="c-p" value={true}>
                  <b className="c-brand-night fz-16">
                    {t('lesson_overview.share_lesson_public.title')}
                  </b>
                  <p className="c-brand-gray-darker fz-16 mb-0 ml-24">
                    {t('lesson_overview.share_lesson_public.description')}
                  </p>
                </RadioField>
              </RadioFieldGroup>
              <div className={displayLinkClass}>
                <input
                  ref={urlTextRef}
                  readOnly
                  className="AssignModal-searchInput ov-h c-p"
                  onKeyDown={copyLink}
                  onClick={copyLink}
                  data-clipboard-text={urls.shareable}
                  value={urls.shareable}
                />
                {Clipboard.isSupported() && (
                  <button
                    ref={copyTextButtonRef}
                    data-clipboard-text={urls.shareable}
                    type="button"
                    onClick={copyLink}
                    className="ws-nw ml-12 mr-12 lh-24 fz-16 td-n c-brand-blue ff-ss h-td-u haf-c-lhci border-0 bs-n bg-inherit"
                  >
                    {t('lesson_overview.copy_link')}
                  </button>
                )}
              </div>
            </>
          )}
          {!lesson.scorm && (
            <>
              <h1 className="ff-ss lh-32 fz-18 c-gray-dark fw-b">
                {t('lesson_overview.pdf_export')}
              </h1>
              <div>
                <Button
                  url={urls.pdfExport}
                  rel="noopener noreferrer"
                  target="_blank"
                  className="ws-nw mt-6 mb-16"
                >
                  <span className="d-f ai-c">
                    <Download className="mr-8 c-midnight-50" size={18} />
                    {t('lesson_overview.download_lesson')}
                  </span>
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal.Content>
      <div className="p-32 pt-0">
        <Fade in={error}>
          <div className="ErrorContainer">
            {error && t('common.server_error')}
          </div>
        </Fade>
        <Fade in={copiedLinkSuccess}>
          <div className="SuccessContainer">
            {copiedLinkSuccess && 'Copied'}
          </div>
        </Fade>
        <ButtonGroup alignEnd className="ai-c">
          <Button onClick={toggleModal}>{t('common.close')}</Button>
        </ButtonGroup>
      </div>
    </>
  );
};

const ShareLessonModal = ({ lesson, shown, toggleModal, error, urls }) => {
  const [isPublic, setIsPublic] = useState(lesson.isPublic);

  return (
    <Modal
      shown={shown}
      onDismiss={toggleModal}
      className="bg-gray-lightest Modal-container"
    >
      <ModalContent
        lesson={lesson}
        toggleModal={toggleModal}
        error={error}
        urls={urls}
        isPublic={isPublic}
        setIsPublic={setIsPublic}
      />
    </Modal>
  );
};

ShareLessonModal.propTypes = {
  shown: PropTypes.bool,
  toggleModal: PropTypes.func,
};

export default ShareLessonModal;
